import { useMemo, useRef, useEffect } from 'react'
import { CellType, CellTypeColor, DEFAULT_CATEGORIES, findCellsOfType } from '../utils/gridHelpers.js'

export default function RouteView({ grid, zones, route, onStartNavigation }) {
  const svgRef = useRef(null)

  if (!grid || !route || route.stops.length === 0) return null

  const height = grid.length
  const width = grid[0]?.length || 0
  const cellSize = 24
  const padding = 12

  // Build zone lookup for cell coloring
  const zoneLookup = useMemo(() => {
    const lookup = new Map()
    for (const zone of zones) {
      const cells = typeof zone.cells === 'string' ? JSON.parse(zone.cells) : zone.cells
      const cat = DEFAULT_CATEGORIES.find(c => c.id === zone.category)
      for (const [x, y] of cells) {
        lookup.set(`${x},${y}`, { color: cat?.color || zone.color })
      }
    }
    return lookup
  }, [zones])

  // Build SVG path from all route segments
  const pathData = useMemo(() => {
    const allPoints = []
    for (const segment of route.paths) {
      for (const point of segment) {
        allPoints.push(point)
      }
    }
    if (allPoints.length === 0) return ''

    const toSvg = (p) => ({
      x: padding + p.x * (cellSize + 1) + cellSize / 2,
      y: padding + p.y * (cellSize + 1) + cellSize / 2,
    })

    const first = toSvg(allPoints[0])
    let d = `M ${first.x} ${first.y}`
    for (let i = 1; i < allPoints.length; i++) {
      const pt = toSvg(allPoints[i])
      d += ` L ${pt.x} ${pt.y}`
    }
    return d
  }, [route.paths])

  // Entrance and checkout positions
  const entranceCells = findCellsOfType(grid, CellType.ENTRANCE)
  const checkoutCells = findCellsOfType(grid, CellType.CHECKOUT)

  const entrance = entranceCells[0] || [0, height - 1]
  const checkout = checkoutCells[0] || [width - 1, height - 1]

  const toPixel = (x, y) => ({
    px: padding + x * (cellSize + 1) + cellSize / 2,
    py: padding + y * (cellSize + 1) + cellSize / 2,
  })

  // Animate path on mount
  useEffect(() => {
    const pathEl = svgRef.current?.querySelector('.route-view__path-line')
    if (pathEl) {
      const length = pathEl.getTotalLength()
      pathEl.style.setProperty('--path-length', length)
      pathEl.classList.add('route-view__path-line--animated')
    }
  }, [pathData])

  const svgWidth = padding * 2 + width * (cellSize + 1)
  const svgHeight = padding * 2 + height * (cellSize + 1)

  return (
    <div className="route-view">
      <div className="route-view__grid-wrapper">
        {/* Grid cells */}
        <div
          className="route-view__grid"
          style={{
            gridTemplateColumns: `repeat(${width}, ${cellSize}px)`,
            gridTemplateRows: `repeat(${height}, ${cellSize}px)`,
            position: 'relative',
          }}
        >
          {grid.map((row, y) =>
            row.map((cell, x) => {
              const zoneInfo = zoneLookup.get(`${x},${y}`)
              return (
                <div
                  key={`${x}-${y}`}
                  className="route-view__cell"
                  style={{
                    backgroundColor: zoneInfo
                      ? zoneInfo.color + '33'
                      : cell !== CellType.EMPTY
                        ? CellTypeColor[cell]
                        : undefined,
                  }}
                />
              )
            })
          )}

          {/* SVG overlay for route path */}
          <svg
            ref={svgRef}
            className="route-view__path-overlay"
            width={svgWidth}
            height={svgHeight}
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          >
            <path className="route-view__path-line" d={pathData} />
          </svg>

          {/* Entrance waypoint */}
          <Waypoint
            x={entrance[0]} y={entrance[1]}
            cellSize={cellSize} padding={padding}
            type="entrance" label="▶"
          />

          {/* Stop waypoints */}
          {route.stops.map((stop, idx) => (
            <Waypoint
              key={stop.category}
              x={stop.walkableX} y={stop.walkableY}
              cellSize={cellSize} padding={padding}
              type="stop" label={idx + 1}
              color={DEFAULT_CATEGORIES.find(c => c.id === stop.category)?.color}
            />
          ))}

          {/* Checkout waypoint */}
          <Waypoint
            x={checkout[0]} y={checkout[1]}
            cellSize={cellSize} padding={padding}
            type="checkout" label="💳"
          />
        </div>
      </div>

      {/* Route stops list */}
      <div className="route-stops">
        <div className="route-stop route-stop--start">
          <div className="route-stop__number" style={{ background: 'var(--color-success)' }}>▶</div>
          <div className="route-stop__info">
            <div className="route-stop__category">Ingang</div>
          </div>
        </div>

        {route.stops.map((stop, idx) => {
          const cat = DEFAULT_CATEGORIES.find(c => c.id === stop.category)
          return (
            <div key={stop.category} className="route-stop">
              <div
                className="route-stop__number"
                style={{ background: cat?.color || 'var(--color-primary)' }}
              >
                {idx + 1}
              </div>
              <div className="route-stop__info">
                <div className="route-stop__category">{cat?.name || stop.category}</div>
                <div className="route-stop__items">{stop.items.join(', ')}</div>
              </div>
            </div>
          )
        })}

        <div className="route-stop route-stop--end">
          <div className="route-stop__number" style={{ background: 'var(--color-warning)' }}>💳</div>
          <div className="route-stop__info">
            <div className="route-stop__category">Kassa</div>
          </div>
        </div>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: 'var(--space-4)',
      }}>
        <span style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-muted)',
        }}>
          Totale afstand: ~{route.totalDistance} stappen
        </span>
        <button className="btn btn--primary btn--lg" onClick={onStartNavigation}>
          🚶 Start navigatie
        </button>
      </div>
    </div>
  )
}

function Waypoint({ x, y, cellSize, padding, type, label, color }) {
  const px = padding + x * (cellSize + 1) + cellSize / 2
  const py = padding + y * (cellSize + 1) + cellSize / 2

  return (
    <div
      className={`route-view__waypoint route-view__waypoint--${type}`}
      style={{
        left: px,
        top: py,
        backgroundColor: color || undefined,
      }}
    >
      {label}
    </div>
  )
}
